import { DeparturePriceBucket } from "@b2bportal/air-shopping-api";
import { useDatepicker, UseDatepickerProps } from "@datepicker-react/hooks";
import { Deal } from "@hopper-proto/hopper/dealfeed/proto/v1/Deal";
import dayjs from "dayjs";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export enum PickerType {
  RANGE = "RANGE",
  DAY = "DAY",
}

export const defaultContext: ReturnType<typeof useDatepicker> & {
  activeMonthIndex: number;
  isActiveMonth: (year: number, month: number) => boolean;
  isStartMonth: boolean;
  isEndMonth: boolean;
  isMonthVisible: (year: number, month: number) => boolean;
  pickerType: PickerType;
  departureDateBuckets?: DeparturePriceBucket[];
  getBucket: (date: Date) => number | undefined;
  deals: Deal[];
  startDate?: Date | null;
  endDate?: Date | null;
  forceDisplayBucketsAndDeals: boolean;
  initialMonth?: Date;
} = {
  firstDayOfWeek: 0,
  activeMonths: [],
  isDateSelected: (_date: Date) => false,
  isDateHovered: (_date: Date) => false,
  isFirstOrLastSelectedDate: (_date: Date) => false,
  isStartDate: (_date: Date) => false,
  isEndDate: (_date: Date) => false,
  isDateBlocked: (_date: Date) => false,
  numberOfMonths: 12,
  isDateFocused: (_date: Date) => false,
  focusedDate: null,
  hoveredDate: null,
  onResetDates: () => null,
  onDateHover: (_date: Date | null) => null,
  onDateSelect: (_date: Date) => null,
  onDateFocus: (_date: Date) => null,
  goToPreviousMonths: () => null,
  goToPreviousMonthsByOneMonth: () => null,
  goToNextMonths: () => null,
  goToNextMonthsByOneMonth: () => null,
  goToDate: (_date: Date) => null,
  goToPreviousYear: (_numYears?: number) => null,
  goToNextYear: (_numYears?: number) => null,
  activeMonthIndex: 0,
  isActiveMonth: (_year: number, _month: number) => false,
  isStartMonth: false,
  isEndMonth: false,
  isMonthVisible: (_year: number, _month: number) => false,
  pickerType: PickerType.RANGE,
  departureDateBuckets: [],
  getBucket: () => undefined,
  deals: [],
  startDate: null,
  endDate: null,
  forceDisplayBucketsAndDeals: false,
  initialMonth: undefined,
};

export const DateCalendarContext = createContext(defaultContext);

export const useDateCalendarContext = () => {
  const ctx = useContext(DateCalendarContext);
  if (ctx === undefined) return defaultContext;
  return { ...ctx };
};

export const DateCalendarContextProvider = ({
  startDate,
  endDate,
  focusedInput,
  onDatesChange,
  pickerType = PickerType.RANGE,
  departureDateBuckets,
  minBookingDays = 2,
  minBookingDate,
  maxBookingDate,
  deals = [],
  forceDisplayBucketsAndDeals = false,
  children,
  initialMonth,
  firstMonth,
  numberOfMonths = 12,
}: PropsWithChildren<
  Pick<
    UseDatepickerProps,
    | "startDate"
    | "endDate"
    | "focusedInput"
    | "onDatesChange"
    | "minBookingDays"
    | "minBookingDate"
    | "maxBookingDate"
  > & {
    pickerType?: PickerType;
    departureDateBuckets?: DeparturePriceBucket[];
    deals?: Deal[];
    forceDisplayBucketsAndDeals?: boolean;
    initialMonth?: Date;
    firstMonth?: Date;
    numberOfMonths?: number;
  }
>) => {
  const result = useDatepicker({
    startDate,
    endDate,
    focusedInput,
    onDatesChange,
    minBookingDate: minBookingDate ?? new Date(),
    maxBookingDate,
    initialVisibleMonth: initialMonth ?? new Date(),
    changeActiveMonthOnSelect: false,
    numberOfMonths,
    firstDayOfWeek: 0,
    minBookingDays, // 1 (the default) means that same day bookings are allowed
  });

  // workaround to keep list of months consistent: the hook lists
  // `numberOfMonths` months from its `startDate`, so this is where the
  // calendar's first month is decided (today unless `firstMonth` is given).
  const listStart = firstMonth ?? new Date();
  const { activeMonths } = useDatepicker({
    startDate: listStart,
    endDate: listStart,
    focusedInput: null,
    onDatesChange: () => null,
    numberOfMonths,
    initialVisibleMonth: initialMonth ?? new Date(),
  });

  function findMonthIndex(date: Date | null | undefined): number {
    if (!date) return 0;
    const index = activeMonths.findIndex((m) =>
      dayjs(m.date).isSame(date, "month")
    );
    return Math.max(Math.min(index, activeMonths.length - 2), 0);
  }

  const [activeMonthIndex, setActiveMonthIndex] = useState(
    findMonthIndex(startDate) || findMonthIndex(initialMonth) || 0
  );

  const isActiveMonth = useCallback(
    (year: number, month: number) =>
      activeMonthIndex >= 0 &&
      activeMonths[activeMonthIndex]?.year === year &&
      activeMonths[activeMonthIndex]?.month === month,
    [activeMonths, activeMonthIndex]
  );

  const isStartMonth = useMemo(() => activeMonthIndex <= 0, [activeMonthIndex]);
  const isEndMonth = useMemo(
    () => activeMonthIndex >= activeMonths.length - 2,
    [activeMonthIndex, activeMonths]
  );

  const goToPreviousMonthsByOneMonth = useCallback(
    () => setActiveMonthIndex(activeMonthIndex - 1),
    [activeMonthIndex]
  );
  const goToNextMonthsByOneMonth = useCallback(
    () => setActiveMonthIndex(activeMonthIndex + 1),
    [activeMonthIndex]
  );

  const isMonthVisible = useCallback(
    (year: number, month: number) => {
      return activeMonths
        .slice(activeMonthIndex, activeMonthIndex + 2)
        .some(
          (activeMonth) =>
            activeMonth.year === year && activeMonth.month === month
        );
    },
    [activeMonths, activeMonthIndex]
  );

  const dateBucketMap = useMemo(() => {
    const map = new Map<string, number>();
    departureDateBuckets?.forEach((bucket, index) => {
      bucket.dates.forEach((date) => {
        map.set(date, index);
      });
    });
    return map;
  }, [departureDateBuckets]);

  const getBucket = useCallback(
    (date: Date) => {
      const dateString = dayjs(date).format("YYYY-MM-DD");
      return dateBucketMap.get(dateString);
    },
    [dateBucketMap]
  );

  return (
    <DateCalendarContext.Provider
      value={{
        ...result,
        activeMonths,
        activeMonthIndex,
        isActiveMonth,
        isStartMonth,
        isEndMonth,
        isMonthVisible,
        goToPreviousMonthsByOneMonth,
        goToNextMonthsByOneMonth,
        pickerType,
        departureDateBuckets,
        getBucket,
        deals,
        startDate,
        endDate,
        forceDisplayBucketsAndDeals,
        initialMonth,
      }}
    >
      {children}
    </DateCalendarContext.Provider>
  );
};
